<template>
  <v-dialog
    v-model="showEditor"
    scrollable
    class="pa-editor"
    :width="preview ? '21cm' : ''"
  >
    <!-- Document -->
    <v-card v-show="preview">
      <v-card-title primary-title>
        <div class="headline">
          {{ value.title }}
        </div>

        <v-spacer />

        <v-btn
          outlined
          @click="preview = false"
        >
          Edit
        </v-btn>
      </v-card-title>

      <v-progress-circular
        v-show="loading"
        indeterminate
        color="primary"
        style="margin:0 auto"
      />

      <!-- eslint-disable-next-line vue/no-v-html -->
      <v-card-text v-html="renderedContent" />
    </v-card>

    <!-- Editor -->
    <v-card v-show="!preview">
      <v-container
        grid-list-lg
        fluid
      >
        <v-layout
          row
          wrap
        >
          <!-- SimpleMDE editor -->
          <v-flex
            xs12
            md8
            lg9
            xl10
            class="pa-editor__cm"
          >
            <textarea ref="editor" />
          </v-flex>

          <!-- Fields -->
          <v-flex
            xs12
            md4
            lg3
            xl2
            class="pa-editor__form"
          >
            <v-text-field
              v-model="value.title"
              hide-details
              label="Title"
            />

            <v-select
              v-model="value.type"
              label="Type"
              :items="allDocumentations"
            />

            <v-select
              v-model="value.project"
              label="Project"
              :items="allProjects"
            />

            <v-card-actions>
              <v-btn
                text
                @click="showEditor = false"
              >
                Close
              </v-btn>

              <v-spacer />

              <v-btn
                color="primary"
                :loading="loading"
                :disabled="!value.title || loading"
                @click="save"
              >
                Save
              </v-btn>
            </v-card-actions>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import marked from 'marked'
import { mapGetters } from 'vuex'

import { uploadFile } from '@/storage'
import Attachment from '@/models/Attachment'
import Projects from '@/models/Projects'

export default {
  props: {
    show: {
      type: Boolean,
      default: false
    },
    document: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      value: {},
      preview: true,
      loading: false,
      simplemde: null,
      renderedContent: null,
      allProjects: new Projects(),
      allDocumentations: []
    }
  },
  computed: {
    ...mapGetters('organizations', [ 'allDocumentationTypes' ]),
    showEditor: {
      get () { return this.show },
      set (val) { if (!val) { this.$emit('update:show', null) } }
    }
  },
  watch: {
    async show (show) {
      if (!show) { return }

      this.renderedContent = null
      this.preview = !!this.document

      if (this.document) {
        this.value = new Attachment()
        await this.value.fetch(this.document)
      } else {
        this.value = new Attachment()
      }

      // Set current project
      if (!this.value.project && this.$route.name.startsWith('project-')) {
        this.value.project = this.$route.params.name
      }

      if (this.value && this.value.downloadURL) {
        this.fetchContent()
      } else {
        this.simplemde.value('')
      }
    }
  },
  async mounted () {
    const { default: SimpleMDE } = await import(/* webpackChunkName: "editor" */ 'simplemde')
    import(/* webpackChunkName: "editor" */ 'simplemde/dist/simplemde.min.css')

    this.simplemde = new SimpleMDE({
      element: this.$refs.editor,
      status: false
    })

    this.allProjects.fetch({ users: this.$currentUser.id })
    this.allDocumentations = this.allDocumentationTypes.map(d => d.name)

    this.fetchContent()
  },
  methods: {
    async save () {
      this.loading = true

      const path = `attachments/${this.value.project}/${this.value.title}.md`

      try {
        const downloadURL = await uploadFile(this.simplemde.value(), path)

        this.value.path = path
        this.value.downloadURL = downloadURL
        this.value.organization = this.$currentOrganization.id

        await this.value.save()
      } catch (err) {
        this.$notifyError('Can\'t upload document', err)
      } finally {
        this.loading = false
      }
    },
    fetchContent () {
      if (!this.value.downloadURL) { return }
      this.loading = true

      axios.get(this.value.downloadURL)
        .then(response => {
          this.simplemde.value(response.data)
          this.renderedContent = marked(response.data)
        })
        .catch(err => {
          this.$notifyError('Error loading document content', err)
        })
        .then(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style lang="scss">

.pa-editor {
  &.A4 {
    width: 21cm;
  }

  &__cm {
    .CodeMirror {
      height: 450px;
    }
  }

  &__form {
    display: flex;
    flex-direction: column;

    > * {
      flex-grow: 0;

      &:last-child {
        margin-top: auto;
      }
    }
  }
}
</style>
